import React from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@frontend/shadcn/components/ui/carousel';
import xss from 'xss';
import { ViewMode } from '../../constants';
import { isVideoUrl } from '@/app/refresh-components/utils/mediaUtils';

interface HeroProps {
  view: ViewMode;
  logo: string;
  title: string;
  description: string;
  joinButtonText: string;
  images: { url: string; alt: string; }[];
  backgroundColor: string;
  bodyTextColor: string;
  bodyTextFontSize: number;
  bodyTextFontFamily: string;
  titleColor: string;
  titleFontSize: number;
  titleFontFamily: string;
  joinButtonColor: string;
  joinButtonTextColor: string;
  joinButtonFontSize: number;
  joinButtonFontFamily: string;
}

export const Hero = ({
  view,
  logo,
  title,
  description,
  joinButtonText,
  images,
  backgroundColor,
  bodyTextColor,
  bodyTextFontSize,
  bodyTextFontFamily,
  titleColor,
  titleFontSize,
  titleFontFamily,
  joinButtonColor,
  joinButtonTextColor,
  joinButtonFontSize,
  joinButtonFontFamily
}: HeroProps) => {
  const isDesktop = view === ViewMode.DESKTOP;

  const titleStyle = {
    color: titleColor,
    fontSize: titleFontSize,
    fontFamily: titleFontFamily,
    fontWeight: 600,
  };

  const bodyStyle = {
    color: bodyTextColor,
    fontSize: bodyTextFontSize,
    fontFamily: bodyTextFontFamily,
  };

  const buttonStyle = {
    background: joinButtonColor,
    color: joinButtonTextColor,
    fontSize: joinButtonFontSize,
    fontFamily: joinButtonFontFamily,
  };

  if (isDesktop) {
    return (
      <div className="w-full min-h-screen relative flex items-center px-16 gap-x-16" style={{ background: backgroundColor }}>
        {/* Logo and Badge */}
        <div className="absolute left-8 top-8">
          <img className="w-10 h-10 rounded-full bg-white" src={logo} alt="Logo" />
        </div>
        {/* Content */}
        <div className="flex-1 flex flex-col justify-center items-start">
          <div
            className="mb-4 w-full break-words"
            style={{ ...titleStyle, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
          >
            {title}
          </div>
          <div
            className="mb-6 max-w-md w-full break-words"
            style={{ ...bodyStyle, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            dangerouslySetInnerHTML={{ __html: xss(description) }}
          />
          <button
            className="px-6 py-2 rounded-lg shadow hover:opacity-90 transition"
            style={buttonStyle}
          >
            {joinButtonText}
          </button>
        </div>
        {/* Carousel */}
        <div className="flex items-center h-[calc(100vh*0.6)]">
          <Carousel
            opts={{
              align: 'start',
              loop: true,
              dragFree: true,
            }}
            className="w-[calc(100vw*0.5)] h-[calc(100vh*0.6)]"
          >
            <CarouselContent className="flex gap-x-6 h-[calc(100vh*0.6)]">
              {images.map((image, index) => (
                <CarouselItem key={index} className="basis-1/2">
                  {isVideoUrl(image.url) ? (
                    <video
                      className="w-full h-[calc(100vh*0.6)] rounded-2xl shadow-lg object-cover"
                      src={image.url}
                      controls
                      playsInline
                      autoPlay
                      muted
                      loop
                    />
                  ) : (
                    <img
                      className="w-full h-[calc(100vh*0.6)] rounded-2xl shadow-lg object-cover"
                      src={image.url}
                      alt={image.alt}
                    />
                  )}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    );
  }

  // Mobile view
  return (
    <div className="w-full min-h-[600px] pb-6" style={{ background: backgroundColor }}>
      {/* Header Section */}
      <div className="w-full flex flex-col items-center pt-6 px-4">
        <div className="flex w-full justify-between items-center">
          <img className="w-10 h-10 rounded-full bg-white" src={logo} alt="Logo" />
        </div>
      </div>
      {/* Main Content */}
      <div className="flex flex-col items-center gap-4 px-4 pt-8">
        <div
          className="text-center leading-tight"
          style={titleStyle}
        >
          {title}
        </div>
        <div
          className="text-center leading-normal w-full max-w-xs break-words overflow-x-hidden"
          style={{ ...bodyStyle, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: xss(description) }}
        />
        <button
          className="mt-2 px-6 py-2 rounded-xl shadow hover:opacity-90 transition"
          style={buttonStyle}
        >
          {joinButtonText}
        </button>
      </div>
      {/* Carousel Section */}
      <div className="w-full flex justify-center mt-8 px-2">
        <Carousel
          opts={{
            align: 'center',
            loop: true,
            dragFree: true,
          }}
          className="w-full max-w-sm"
        >
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index} className="px-2">
                <div className="rounded-2xl overflow-hidden shadow-md">
                  {isVideoUrl(image.url) ? (
                    <video
                      className="w-full aspect-[3/4] object-cover"
                      src={image.url}
                      controls
                      playsInline
                      autoPlay
                      muted
                      loop
                    />
                  ) : (
                    <img
                      className="w-full aspect-[3/4] object-cover"
                      src={image.url}
                      alt={image.alt}
                    />
                  )}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  );
};
